import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { Calendar, TrendingUp, BarChart3, Activity } from 'lucide-react';

interface DataPoint {
  date: string;
  impressions?: number;
  clicks?: number;
  conversions?: number;
  spend?: number;
  revenue?: number;
  ctr?: number;
  cpc?: number;
  roas?: number;
}

interface PerformanceChartProps {
  data: DataPoint[];
  metrics?: string[];
  chartType?: 'line' | 'area' | 'bar';
  loading?: boolean;
  title?: string;
  height?: number;
}

const METRIC_COLORS: Record<string, string> = {
  impressions: '#3B82F6',
  clicks: '#10B981',
  conversions: '#8B5CF6',
  spend: '#EF4444',
  revenue: '#F59E0B',
  ctr: '#06B6D4',
  cpc: '#EC4899',
  roas: '#14B8A6'
};

const METRIC_LABELS: Record<string, string> = {
  impressions: 'Impressões',
  clicks: 'Cliques',
  conversions: 'Conversões',
  spend: 'Gasto',
  revenue: 'Receita',
  ctr: 'CTR',
  cpc: 'CPC',
  roas: 'ROAS'
};

const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  metrics = ['impressions', 'clicks'],
  chartType = 'line',
  loading = false,
  title,
  height = 300
}) => {
  const { t } = useTranslation();
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(metrics);
  const [currentChartType, setCurrentChartType] = useState(chartType);

  const toggleMetric = (metric: string) => {
    if (selectedMetrics.includes(metric)) {
      if (selectedMetrics.length > 1) {
        setSelectedMetrics(selectedMetrics.filter(m => m !== metric));
      }
    } else {
      setSelectedMetrics([...selectedMetrics, metric]);
    }
  };

  const formatYAxis = (value: number, metric: string) => {
    if (metric === 'spend' || metric === 'revenue' || metric === 'cpc') {
      return `R$${value.toFixed(0)}`;
    }
    if (metric === 'ctr' || metric === 'roas') {
      return `${value.toFixed(1)}%`;
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toString();
  };

  const formatTooltipValue = (value: number, name: string) => {
    if (name === 'spend' || name === 'revenue') {
      return [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`, METRIC_LABELS[name]];
    }
    if (name === 'cpc') {
      return [`R$ ${value.toFixed(2)}`, METRIC_LABELS[name]];
    }
    if (name === 'ctr') {
      return [`${value.toFixed(2)}%`, METRIC_LABELS[name]];
    }
    if (name === 'roas') {
      return [`${value.toFixed(2)}x`, METRIC_LABELS[name]];
    }
    return [value.toLocaleString('pt-BR'), METRIC_LABELS[name]];
  };

  // Sem números no período (nenhum anúncio rodando): mostra um aviso em vez de um gráfico vazio
  const hasData = (data || []).some((row: any) => Object.keys(METRIC_COLORS).some((m) => Number(row?.[m]) > 0));

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        {title && <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4 animate-pulse" />}
        <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" />
      </div>
    );
  }

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 5, right: 8, left: -12, bottom: 5 }
    };

    const renderLines = () =>
      selectedMetrics.map(metric => (
        <Line
          key={metric}
          type="monotone"
          dataKey={metric}
          stroke={METRIC_COLORS[metric]}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 6 }}
        />
      ));

    const renderAreas = () =>
      selectedMetrics.map((metric, index) => (
        <Area
          key={metric}
          type="monotone"
          dataKey={metric}
          stackId={index === 0 ? "1" : undefined}
          stroke={METRIC_COLORS[metric]}
          fill={METRIC_COLORS[metric]}
          fillOpacity={0.3}
        />
      ));

    const renderBars = () =>
      selectedMetrics.map(metric => (
        <Bar
          key={metric}
          dataKey={metric}
          fill={METRIC_COLORS[metric]}
          radius={[4, 4, 0, 0]}
        />
      ));

    switch (currentChartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis dataKey="date" className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} />
            <YAxis className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tooltip-bg, #fff)',
                borderColor: 'var(--tooltip-border, #e5e7eb)'
              }}
              formatter={formatTooltipValue}
            />
            <Legend formatter={(value: string) => METRIC_LABELS[value] || value} />
            {renderAreas()}
          </AreaChart>
        );
      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis dataKey="date" className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} />
            <YAxis className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tooltip-bg, #fff)',
                borderColor: 'var(--tooltip-border, #e5e7eb)'
              }}
              formatter={formatTooltipValue}
            />
            <Legend formatter={(value: string) => METRIC_LABELS[value] || value} />
            {renderBars()}
          </BarChart>
        );
      default:
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
            <XAxis dataKey="date" className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} />
            <YAxis className="text-gray-500 dark:text-gray-400" tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--tooltip-bg, #fff)',
                borderColor: 'var(--tooltip-border, #e5e7eb)'
              }}
              formatter={formatTooltipValue}
            />
            <Legend formatter={(value: string) => METRIC_LABELS[value] || value} />
            {renderLines()}
          </LineChart>
        );
    }
  };

  if (!hasData) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
        {title && <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h3>}
        <div className="flex flex-col items-center justify-center text-center py-10 sm:py-16">
          <TrendingUp className="h-10 w-10 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="font-medium text-gray-700 dark:text-gray-200">
            {t('dashboard.chartEmptyTitle', 'Ainda não há números neste período')}
          </p>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm">
            {t('dashboard.chartEmptyText', 'Quando seus anúncios começarem a rodar, o gráfico de visualizações, cliques e vendas aparece aqui.')}
          </p>
          <Link to="/campaigns" className="mt-4 inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
            {t('dashboard.chartEmptyAction', 'Criar um anúncio')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-4">
        {title && (
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
        )}
        <div className="flex items-center space-x-2">
          {/* Chart Type Switcher */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            <button
              onClick={() => setCurrentChartType('line')}
              className={`p-1.5 rounded ${
                currentChartType === 'line'
                  ? 'bg-white dark:bg-gray-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title={t('reports.charts.line')}
            >
              <TrendingUp className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentChartType('area')}
              className={`p-1.5 rounded ${
                currentChartType === 'area'
                  ? 'bg-white dark:bg-gray-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title={t('reports.charts.area')}
            >
              <Activity className="h-4 w-4" />
            </button>
            <button
              onClick={() => setCurrentChartType('bar')}
              className={`p-1.5 rounded ${
                currentChartType === 'bar'
                  ? 'bg-white dark:bg-gray-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title={t('reports.charts.bar')}
            >
              <BarChart3 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Metric Toggles */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.keys(METRIC_COLORS).map(metric => (
          <button
            key={metric}
            onClick={() => toggleMetric(metric)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              selectedMetrics.includes(metric)
                ? 'text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            style={
              selectedMetrics.includes(metric)
                ? { backgroundColor: METRIC_COLORS[metric] }
                : {}
            }
          >
            {METRIC_LABELS[metric]}
          </button>
        ))}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        {renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default PerformanceChart;
